import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { safeParseInt, setDocumentTitle } from "shared/util"

import { Alert, StackProps } from "@mui/material"

import { SearchProvider } from "features/search/context"
import { useTenant } from "features/tenant/context"

import { useGetSearchDetail } from "interface/search"
import { useGetKojin } from "interface/kojin"
import { useGetAuthor } from "interface/author"

import { ErrorBlock } from "components/controls/errorInfo"
import { Loading } from "components/controls/loading"
import { useSideMenu } from "components/sideMenu/container"
import { useTopBar } from "components/topBar/container"
import { BreadCrumbItem } from "components/topBar/types"

import { KojinPreviewBody } from "./preview/body"
import { useGetEvaluationForm } from "interface/evaluationForm"

type KojinPreviewPageProps = {
  searchId?: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const KojinPreviewPage = ({
	searchId,
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage }
}: KojinPreviewPageProps) => {
  const tenant = useTenant()
	const { kojinCode } = useParams()

  const searchState = useGetSearchDetail(searchId)
  const evaluationFormStatus = useGetEvaluationForm(searchState.data?.evaluationFormId)
  
  const kojinState = useGetKojin(searchId, undefined, kojinCode)
  const authorState = useGetAuthor(searchId, kojinState.data?.authorId)
  const error = useMemo(
    () => [searchState, kojinState, evaluationFormStatus].flatMap(state => state.error ?? []),
    [searchState, kojinState, evaluationFormStatus]
  )
  useEffect(
    () => {
			const search = searchState.data
      const kojin = kojinState.data
      setDocumentTitle(
        kojin?.isiName,
        search?.title,
        tenant.tenantName
      )
      const crumbs: BreadCrumbItem[] = []
      if (search?.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
			if (search) {
				crumbs.push({
					label: search.title,
					url: `/${tenant.domain}/search/${search.searchId}/home`
				})
      }
      if (kojin) {
				crumbs.push({
					label: kojin.isiName ?? kojin.isiNameKana ?? 'no name'
				})
      }
      setBreadCrumbs(crumbs)
      setActivePage('kojin')
    },
    [tenant, searchState, kojinState, setBreadCrumbs, setActivePage]
  )
  if (!kojinCode) return <Alert severity="error" sx={{ wordBreak: 'break-all' }}>パラメータ不正</Alert>
  if (searchState.loading || kojinState.loading) return <Loading/>
  if (error.length > 0) return <ErrorBlock error={error}/>
  if (!kojinState.data) return <></>

	return (
    <SearchProvider search={searchState.data} evaluationForm={evaluationFormStatus.data}>
			<KojinPreviewBody kojin={kojinState.data} author={authorState.data}/>
		</SearchProvider>
	)
}

type AuthorPreviewPageProps = {
  searchId: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const AuthorPreviewPage = ({
	searchId,
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage }
}: AuthorPreviewPageProps) => {
  const tenant = useTenant()
	const { authorId: authorIdStr } = useParams()
  const authorId = safeParseInt(authorIdStr) ?? null
  const searchState = useGetSearchDetail(searchId)
  const authorState = useGetAuthor(searchId, authorId)
  const error = useMemo(
    () => [searchState, authorState].flatMap(state => state.error ?? []),
    [searchState, authorState]
  )
  useEffect(
    () => {
			const search = searchState.data
      const author = authorState.data
      setDocumentTitle(
        author?.isiName,
        search?.title,
        tenant.tenantName
      )
      const crumbs: BreadCrumbItem[] = []
      if (search?.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
			if (search) {
				crumbs.push({
					label: search.title,
					url: `/${tenant.domain}/search/${search.searchId}/home`
				})
      }
      if (author) {
				crumbs.push({
					label: author.isiName ?? author.isiNameKana ?? 'no name'
				})
      }
      setBreadCrumbs(crumbs)
      setActivePage('kojin')
    },
    [tenant, searchState, authorState, setBreadCrumbs, setActivePage]
  )
  if (!authorId) return <Alert severity="error" sx={{ wordBreak: 'break-all' }}>パラメータ不正</Alert>
  if (searchState.loading || authorState.loading) return <Loading/>
  if (error.length > 0) return <ErrorBlock error={error}/>
  if (!searchState.data || !authorState.data) return <></>

	return (
    <SearchProvider search={searchState.data}>
			<KojinPreviewBody kojin={null} author={authorState.data}/>
		</SearchProvider>
	)
}
